// useCurtainwallState.js
// Phase 6: Curtain Wall division - UI state and simple handlers
// API handlers remain in ProjectDetail.js due to external dependencies

import { useState, useCallback } from 'react';

export const useCurtainwallState = () => {
  // ============================================
  // OPERATION LOADING STATE (prevents double-submit)
  // ============================================
  const [operationLoading, setOperationLoading] = useState(false);
  
  // ============================================
  // SELECTION STATES
  // ============================================
  const [selectedCurtainwallTypeIds, setSelectedCurtainwallTypeIds] = useState([]);
  const [selectedCWGlassTypeIds, setSelectedCWGlassTypeIds] = useState([]);
  const [selectedCWMetalPanelTypeIds, setSelectedCWMetalPanelTypeIds] = useState([]);
  const [selectedCWPanelListIds, setSelectedCWPanelListIds] = useState([]);
  const [selectedCurtainwallElevationIds, setSelectedCurtainwallElevationIds] = useState([]);
  
  // Expanded states for nested views
  const [expandedCWGlassTypeIds, setExpandedCWGlassTypeIds] = useState([]);
  const [expandedCWMetalPanelIds, setExpandedCWMetalPanelIds] = useState([]);
  
  // ============================================
  // ELEVATION STATES
  // ============================================
  const [selectedCurtainwallElevation, setSelectedCurtainwallElevation] = useState(null);
  
  // ============================================
  // SEARCH, FILTER, SORT STATES
  // ============================================
  const [cwScheduleSearch, setCwScheduleSearch] = useState('');
  const [cwScheduleFilters, setCwScheduleFilters] = useState({ type: '', location: '' });
  const [cwScheduleSortBy, setCwScheduleSortBy] = useState('createdAt_desc');
  
  // ============================================
  // CURTAIN WALL TYPE DIALOG STATE
  // ============================================
  const [showCWTypeDialog, setShowCWTypeDialog] = useState(false);
  const [editingCWType, setEditingCWType] = useState(null);
  const [newCWType, setNewCWType] = useState({
    name: '',
    manufacturer: '',
    model: '',
    material: '',
    finishType: '',
    interiorColor: '',
    exteriorColor: '',
    sectionDimWidth_in: null,
    sectionDimHeight_in: null,
    roWidth_in: null,
    roHeight_in: null,
    productWarrantyYears: null,
    hardwareWarrantyYears: null,
    installationWarrantyYears: null,
    installationManual: '',
    cleaningManual: ''
  });
  
  // ============================================
  // CURTAIN WALL PICTURE/DRAWING STATE
  // ============================================
  const [pendingCWTypePicture, setPendingCWTypePicture] = useState(null);
  const [cwTypePictureUploading, setCWTypePictureUploading] = useState(null);
  const [cwTypePictureUrl, setCWTypePictureUrl] = useState(null);
  const [showCWDrawingDialog, setShowCWDrawingDialog] = useState(false);
  const [selectedCWTypeForView, setSelectedCWTypeForView] = useState(null);
  const [showCWDrawingMarkerDialog, setShowCWDrawingMarkerDialog] = useState(false);
  const [pendingCWMarker, setPendingCWMarker] = useState(null);
  const [activeCWTypeForMarker, setActiveCWTypeForMarker] = useState(null);
  
  // ============================================
  // BULK MARKER STATES
  // ============================================
  const [cwBulkMarkerMode, setCwBulkMarkerMode] = useState(false);
  const [cwBulkMarkerType, setCwBulkMarkerType] = useState('');
  const [pendingCwBulkMarkers, setPendingCwBulkMarkers] = useState([]);
  const [savingCwBulkMarkers, setSavingCwBulkMarkers] = useState(false);

  // ============================================
  // SIMPLE HANDLERS (No API calls)
  // ============================================

  // Reset new CW type form
  const resetNewCWType = useCallback(() => {
    setNewCWType({
      name: '',
      manufacturer: '',
      model: '',
      material: '',
      finishType: '',
      interiorColor: '',
      exteriorColor: '',
      sectionDimWidth_in: null,
      sectionDimHeight_in: null,
      roWidth_in: null,
      roHeight_in: null,
      productWarrantyYears: null,
      hardwareWarrantyYears: null,
      installationWarrantyYears: null,
      installationManual: '',
      cleaningManual: ''
    });
    setPendingCWTypePicture(null);
  }, []);

  // Handle edit CW type - populates form
  const handleEditCWType = useCallback((cwType) => {
    setEditingCWType(cwType);
    setNewCWType({
      name: cwType.name || '',
      manufacturer: cwType.manufacturer || '',
      model: cwType.model || '',
      material: cwType.material || '',
      finishType: cwType.finishType || '',
      interiorColor: cwType.interiorColor || '',
      exteriorColor: cwType.exteriorColor || '',
      sectionDimWidth_in: cwType.sectionDimWidth_in || null,
      sectionDimHeight_in: cwType.sectionDimHeight_in || null,
      roWidth_in: cwType.roWidth_in || null,
      roHeight_in: cwType.roHeight_in || null,
      productWarrantyYears: cwType.productWarrantyYears || null,
      hardwareWarrantyYears: cwType.hardwareWarrantyYears || null,
      installationWarrantyYears: cwType.installationWarrantyYears || null,
      installationManual: cwType.installationManual || '',
      cleaningManual: cwType.cleaningManual || ''
    });
    setShowCWTypeDialog(true);
  }, []);

  // Handle close CW type dialog
  const handleCloseCWTypeDialog = useCallback((open) => {
    setShowCWTypeDialog(open);
    if (!open) {
      setEditingCWType(null);
      resetNewCWType();
    }
  }, [resetNewCWType]);

  // Bulk marker handlers
  const handleCancelCwBulkMarkers = useCallback(() => {
    setCwBulkMarkerMode(false);
    setCwBulkMarkerType('');
    setPendingCwBulkMarkers([]);
  }, []);

  const handleRemovePendingCwMarker = useCallback((markerId) => {
    setPendingCwBulkMarkers(prev => prev.filter(m => m.id !== markerId));
  }, []);

  // Clear all selections
  const clearCurtainwallSelections = useCallback(() => {
    setSelectedCurtainwallTypeIds([]);
    setSelectedCWGlassTypeIds([]);
    setSelectedCWMetalPanelTypeIds([]);
    setSelectedCWPanelListIds([]);
    setSelectedCurtainwallElevationIds([]);
  }, []);

  // ============================================
  // RETURN ALL STATE AND HANDLERS
  // ============================================
  return {
    // Operation loading (prevents double-submit)
    operationLoading,
    setOperationLoading,
    
    // Selection states
    selectedCurtainwallTypeIds, setSelectedCurtainwallTypeIds,
    selectedCWGlassTypeIds, setSelectedCWGlassTypeIds,
    selectedCWMetalPanelTypeIds, setSelectedCWMetalPanelTypeIds,
    selectedCWPanelListIds, setSelectedCWPanelListIds,
    selectedCurtainwallElevationIds, setSelectedCurtainwallElevationIds,
    expandedCWGlassTypeIds, setExpandedCWGlassTypeIds,
    expandedCWMetalPanelIds, setExpandedCWMetalPanelIds,
    
    // Elevation states
    selectedCurtainwallElevation, setSelectedCurtainwallElevation,
    
    // Search, filter, sort
    cwScheduleSearch, setCwScheduleSearch,
    cwScheduleFilters, setCwScheduleFilters,
    cwScheduleSortBy, setCwScheduleSortBy,
    
    // CW Type dialog
    showCWTypeDialog, setShowCWTypeDialog,
    editingCWType, setEditingCWType,
    newCWType, setNewCWType,
    
    // Picture/Drawing states
    pendingCWTypePicture, setPendingCWTypePicture,
    cwTypePictureUploading, setCWTypePictureUploading,
    cwTypePictureUrl, setCWTypePictureUrl,
    showCWDrawingDialog, setShowCWDrawingDialog,
    selectedCWTypeForView, setSelectedCWTypeForView,
    showCWDrawingMarkerDialog, setShowCWDrawingMarkerDialog,
    pendingCWMarker, setPendingCWMarker,
    activeCWTypeForMarker, setActiveCWTypeForMarker,
    
    // Bulk marker states
    cwBulkMarkerMode, setCwBulkMarkerMode,
    cwBulkMarkerType, setCwBulkMarkerType,
    pendingCwBulkMarkers, setPendingCwBulkMarkers,
    savingCwBulkMarkers, setSavingCwBulkMarkers,
    
    // Simple handlers
    resetNewCWType,
    handleEditCWType,
    handleCloseCWTypeDialog,
    handleCancelCwBulkMarkers,
    handleRemovePendingCwMarker,
    clearCurtainwallSelections
  };
};

export default useCurtainwallState;
